import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSubscription } from '../../context/SubscriptionContext';
import { productsApi, categoriesApi } from '../../services/apiService';
import SellerDashboardLayout from '../../components/SellerDashboardLayout/SellerDashboardLayout';
import HideRemoveDropdownModal from '../../components/HideRemoveDropdownModal';
import Pagination from '../../components/Pagination/Pagination';
import './ProductManagePage.css';

// Helper to convert image URLs - fix localhost and relative paths
const fixImageUrl = (url) => {
  if (!url) return null;
  // Fix old localhost URLs
  if (url.startsWith('http://localhost')) {
    try { return `https://gulfconstructor.heloix.com${new URL(url).pathname}`; } catch { return null; }
  }
  // Fix relative paths
  if (url.startsWith('/uploads/')) {
    return `https://gulfconstructor.heloix.com${url}`;
  }
  return url;
};

// Helper function to get product image from image field or images array
const getProductImage = (product) => {
  // Check images array first (could be string or array) - this has the actual uploaded images
  if (product.images) {
    try {
      const imagesArray = typeof product.images === 'string' 
        ? JSON.parse(product.images) 
        : product.images;
      if (Array.isArray(imagesArray) && imagesArray.length > 0 && imagesArray[0]) {
        return fixImageUrl(imagesArray[0]);
      }
    } catch (e) {
      console.error('Error parsing product images:', e);
    }
  }
  
  // Then check main image field - but skip invalid URLs
  if (product.image) {
    const img = product.image;
    // Skip placeholder or invalid URLs
    if (img.includes('da821e6a6c344d588c41324f62b0dda825ecff92') || 
        img.includes('via.placeholder.com') ||
        img.includes('pexels.com/photo/') ||  // This is page URL not image URL
        img.includes('placeholder.jpg') ||
        (!img.match(/\.(jpg|jpeg|png|gif|webp|svg)/i) && !img.includes('unsplash.com'))) {
      return null;
    }
    return fixImageUrl(img);
  }
  
  return null;
};

const ProductManagePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getFeature, planDisplayName, isAdmin } = useSubscription();
  // Admin has unlimited products
  const isAdminUser = isAdmin();
  const productLimit = isAdminUser ? 999999 : (getFeature('productPostingLimit') || 10);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [activeStatus, setActiveStatus] = useState('Active');
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownState, setDropdownState] = useState({ isOpen: false, productId: null, position: null });
  const [productStatuses, setProductStatuses] = useState({});
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  // Real data states
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalProducts, setTotalProducts] = useState(0);

  // Fetch products from API
  const fetchProducts = useCallback(async () => {
    if (!user?.id) {
      setProducts([]);
      setTotalProducts(0);
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Get only current user's products (showAll for seller's own products)
      const params = {
        limit: 100,
        offset: 0
      };
      
      if (activeStatus !== 'All') {
        params.status = activeStatus.toLowerCase();
      }
      
      if (selectedCategory) {
        params.category = selectedCategory;
      }
      
      if (searchTerm) {
        params.search = searchTerm;
      }
      
      // Get products by user ID - only show current user's products
      const response = await productsApi.getByUserId(user.id, params);
      const productData = Array.isArray(response) ? response : (response.products || []);
      setProducts(productData);
      setTotalProducts(response.total || productData.length);
    } catch (err) {
      console.error('Error fetching products:', err);
      setError('Failed to fetch products');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id, activeStatus, selectedCategory, searchTerm]);

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const response = await categoriesApi.getAll();
      setCategories(Array.isArray(response) ? response : []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, [fetchProducts, fetchCategories]);

  // Filter products based on search (client-side for instant feedback)
  const filteredProducts = products.filter(product => {
    const matchesSearch = !searchTerm || 
      product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.category?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = activeStatus === 'All' || product.status?.toLowerCase() === activeStatus.toLowerCase();
    const matchesCategory = !selectedCategory || product.categoryId === parseInt(selectedCategory);
    return matchesSearch && matchesStatus && matchesCategory;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedProducts = filteredProducts.slice(startIndex, startIndex + itemsPerPage);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeStatus, selectedCategory]);

  const handleSelectAll = () => {
    // Toggle: if all are selected, deselect all; otherwise select all
    if (paginatedProducts.length > 0 && selectedProducts.length === paginatedProducts.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(paginatedProducts.map(p => p.id));
    }
  };

  const handleSelectProduct = (productId) => {
    if (selectedProducts.includes(productId)) {
      setSelectedProducts(selectedProducts.filter(id => id !== productId));
    } else {
      setSelectedProducts([...selectedProducts, productId]);
    }
  };

  const handleActionClick = (e, productId) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownState({
      isOpen: true,
      productId,
      position: { x: rect.left - 80, y: rect.bottom + 5 }
    });
  };

  const handleDropdownClose = () => {
    setDropdownState({ isOpen: false, productId: null, position: null });
  };

  const handleStatusSelect = async (status) => {
    if (dropdownState.productId) {
      try {
        await productsApi.updateStatus(dropdownState.productId, status);
        // Refresh products list
        fetchProducts();
        handleDropdownClose();
      } catch (err) {
        console.error('Error updating status:', err);
        alert('Failed to update product status');
      }
    }
  };

  // Delete single product
  const handleDeleteProduct = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await productsApi.delete(productId);
        fetchProducts();
        handleDropdownClose();
      } catch (err) {
        console.error('Error deleting product:', err);
        alert('Failed to delete product');
        handleDropdownClose();
      }
    } else {
      handleDropdownClose();
    }
  };

  // Bulk delete selected products
  const handleBulkDelete = async () => {
    if (selectedProducts.length === 0) {
      alert('Please select products to delete');
      return;
    }
    
    if (window.confirm(`Are you sure you want to delete ${selectedProducts.length} products?`)) {
      try {
        await productsApi.bulkDelete(selectedProducts);
        setSelectedProducts([]);
        fetchProducts();
      } catch (err) {
        console.error('Error bulk deleting:', err);
        alert('Failed to delete products');
      }
    }
  };

  // View product details
  const handleViewDetails = (productId) => {
    navigate(`/product/${productId}`);
  };

  // Edit product
  const handleEditProduct = (productId) => {
    navigate(`/product-input?edit=${productId}`);
  };

  // Promote button click
  const handlePromote = (productId, isPromoted) => {
    if (!isPromoted) {
      navigate('/membership-packages');
    }
  };

  // Chat & Inquire click
  const handleChatInquire = (productId) => {
    navigate('/messages');
  };

  // Pagination handlers
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Status filter handler
  const handleStatusFilter = (status) => {
    setActiveStatus(status);
    setShowStatusDropdown(false);
  };

  // Category filter handler
  const handleCategoryFilter = (categoryId) => {
    setSelectedCategory(categoryId);
    setShowFilterDropdown(false);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.status-dropdown')) {
        setShowStatusDropdown(false);
      }
      if (!e.target.closest('.filter-dropdown')) {
        setShowFilterDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  };

  return (
    <SellerDashboardLayout>
      <div className="product-manage-content">
        {/* Product Limit Warning Banner */}
        {totalProducts >= productLimit && (
          <div className="product-limit-warning">
            <span className="warning-icon">⚠️</span>
            <span>You've reached your product limit ({totalProducts}/{productLimit}). </span>
            <Link to="/membership-packages" className="upgrade-link">Upgrade your plan</Link>
            <span> to add more products.</span>
          </div>
        )}
        
        <div className="page-header">
          <div className="page-title-section">
            <h1 className="page-title">Product Listing</h1>
            <p className="page-description">
              Manage your leads, track orders, and grow your business — all in one place.
            </p>
          </div>
          <div className="header-actions">
            <div className={`product-limit-badge ${totalProducts >= productLimit ? 'limit-reached' : ''}`}>
              <span className="limit-label">Products:</span>
              <span className="limit-count">{totalProducts} / {productLimit}</span>
              <span className="plan-name">({planDisplayName || 'Basic'})</span>
            </div>
            <Link 
              to="/product-input" 
              className={`create-product-btn ${totalProducts >= productLimit ? 'disabled' : ''}`}
              onClick={(e) => {
                if (totalProducts >= productLimit) {
                  e.preventDefault();
                  alert(`You've reached your product limit (${productLimit}). Please upgrade your membership plan.`);
                }
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 5V19" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M5 12H19" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>Create Product</span>
            </Link>
          </div>
        </div>

        <div className="product-list-section">
          <div className="list-header">
            <h2 className="list-title">Product Lists</h2>
            <div className="list-controls">
              <div className="search-box">
                <input 
                  type="text" 
                  placeholder="Search" 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14.167 14.167L17.5003 17.5003" stroke="#969696" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667Z" stroke="#969696" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              
              {/* Status Filter - Native Select */}
              <div className="select-wrapper">
                <select 
                  className="filter-select"
                  value={activeStatus}
                  onChange={(e) => handleStatusFilter(e.target.value)}
                >
                  <option value="Active">Active</option>
                  <option value="All">All</option>
                  <option value="Pending">Pending</option>
                  <option value="Inactive">Inactive</option>
                  <option value="Hidden">Hidden</option>
                </select>
              </div>
              
              {/* Filter By Category - Native Select */}
              <div className="select-wrapper category-select">
                <svg className="filter-icon" width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18.0171 3.86955C17.9209 3.64729 17.7615 3.45822 17.5587 3.32584C17.3559 3.19347 17.1186 3.12363 16.8764 3.12502H3.12644C2.88452 3.1255 2.64792 3.19617 2.44537 3.32846C2.24282 3.46076 2.08301 3.64899 1.98532 3.87032C1.88764 4.09165 1.85629 4.33658 1.89507 4.57538C1.93385 4.81418 2.04109 5.03659 2.20379 5.21565L2.21004 5.22268L7.50144 10.8727V16.875C7.50139 17.1013 7.56274 17.3233 7.67894 17.5174C7.79514 17.7115 7.96185 17.8704 8.16128 17.9772C8.36071 18.084 8.58539 18.1347 8.81136 18.1239C9.03733 18.113 9.25613 18.0411 9.44441 17.9156L11.9444 16.2485C12.1158 16.1343 12.2563 15.9796 12.3535 15.798C12.4507 15.6165 12.5015 15.4137 12.5014 15.2078V10.8727L17.7936 5.22268L17.7999 5.21565C17.9643 5.03741 18.0726 4.8147 18.1112 4.57531C18.1498 4.33592 18.1171 4.09045 18.0171 3.86955ZM11.4218 10.2016C11.3136 10.3162 11.2528 10.4675 11.2514 10.625V15.2078L8.75144 16.875V10.625C8.75149 10.4663 8.69116 10.3135 8.58269 10.1977L3.12644 4.37502H16.8764L11.4218 10.2016Z" fill="#333333"/>
                </svg>
                <select 
                  className="filter-select with-icon"
                  value={selectedCategory}
                  onChange={(e) => handleCategoryFilter(e.target.value)}
                >
                  <option value="">Filter By Category</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>{category.name}</option>
                  ))}
                </select>
              </div>
              
              {selectedProducts.length > 0 && (
                <button className="bulk-delete-btn" onClick={handleBulkDelete}>
                  Delete Selected ({selectedProducts.length})
                </button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="loading-state">
              <p>Loading products...</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <p>{error}</p>
              <button onClick={fetchProducts}>Retry</button>
            </div>
          ) : (
          <div className="product-table figma-table">
            <div className="table-header">
              <div className="col-checkbox">
                <label className="custom-checkbox-wrapper">
                  <input 
                    type="checkbox" 
                    checked={paginatedProducts.length > 0 && selectedProducts.length === paginatedProducts.length}
                    onChange={handleSelectAll}
                  />
                  <span className="checkmark"></span>
                </label>
                <span>All</span>
              </div>
              <div className="col-image">Image</div>
              <div className="col-date">Post Date</div>
              <div className="col-name">Listing Name</div>
              <div className="col-category">Product Category</div>
              <div className="col-subcategory">Price Range</div>
              <div className="col-inquire">MOQ</div>
              <div className="col-chat">Unit</div>
              <div className="col-promote">Promote</div>
              <div className="col-status">Status</div>
              <div className="col-details">Details</div>
              <div className="col-action">Action</div>
            </div>

            <div className="table-body">
              {paginatedProducts.length > 0 ? (
                paginatedProducts.map((product) => (
                  <div key={product.id} className="table-row">
                    <div className="col-checkbox">
                      <label className="custom-checkbox-wrapper">
                        <input 
                          type="checkbox" 
                          checked={selectedProducts.includes(product.id)}
                          onChange={() => handleSelectProduct(product.id)}
                        />
                        <span className="checkmark"></span>
                      </label>
                    </div>
                    <div className="col-image">
                      <img 
                        src={getProductImage(product) || 'https://api.builder.io/api/v1/image/assets/TEMP/da821e6a6c344d588c41324f62b0dda825ecff92?width=112'} 
                        alt="Product" 
                        onError={(e) => { e.target.onerror = null; e.target.src = 'https://api.builder.io/api/v1/image/assets/TEMP/da821e6a6c344d588c41324f62b0dda825ecff92?width=112'; }}
                      />
                    </div>
                    <div className="col-date">{formatDate(product.createdAt)}</div>
                    <div className="col-name">{((product.name || product.description || '').split(' ').slice(0, 2).join(' ')) + ((product.name || product.description || '').split(' ').length > 2 ? '...' : '')}</div>
                    <div className="col-category">{product.category?.name || '-'}</div>
                    <div className="col-subcategory">{product.priceMin && product.priceMax ? `$${product.priceMin} - $${product.priceMax}` : '-'}</div>
                    <div className="col-inquire">{product.moq || '-'}</div>
                    <div className="col-chat">{product.unit || '-'}</div>
                    <div className="col-promote">
                      {product.isFeatured ? (
                        <span className="promoted-link">Promoted</span>
                      ) : (
                        <span className="promote-link" onClick={() => handlePromote(product.id, false)}>Promote</span>
                      )}
                    </div>
                    <div className="col-status">
                      <span className={`status-badge ${(product.status || 'pending').toLowerCase()}`}>
                        {product.status ? product.status.charAt(0).toUpperCase() + product.status.slice(1) : 'Pending'}
                      </span>
                    </div>
                    <div className="col-details">
                      <span className="details-link" onClick={() => handleViewDetails(product.id)}>Details</span>
                    </div>
                    <div className="col-action">
                      <button className="action-btn" onClick={(e) => handleActionClick(e, product.id)}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M10.5 4.5C10.5 3.90326 10.7371 3.33097 11.159 2.90901C11.581 2.48705 12.1533 2.25 12.75 2.25C13.3467 2.25 13.919 2.48705 14.341 2.90901C14.7629 3.33097 15 3.90326 15 4.5C15 5.09674 14.7629 5.66903 14.341 6.09099C13.919 6.51295 13.3467 6.75 12.75 6.75C12.1533 6.75 11.581 6.51295 11.159 6.09099C10.7371 5.66903 10.5 5.09674 10.5 4.5ZM10.5 12C10.5 11.4033 10.7371 10.831 11.159 10.409C11.581 9.98705 12.1533 9.75 12.75 9.75C13.3467 9.75 13.919 9.98705 14.341 10.409C14.7629 10.831 15 11.4033 15 12C15 12.5967 14.7629 13.169 14.341 13.591C13.919 14.0129 13.3467 14.25 12.75 14.25C12.1533 14.25 11.581 14.0129 11.159 13.591C10.7371 13.169 10.5 12.5967 10.5 12ZM10.5 19.5C10.5 18.9033 10.7371 18.331 11.159 17.909C11.581 17.4871 12.1533 17.25 12.75 17.25C13.3467 17.25 13.919 17.4871 14.341 17.909C14.7629 18.331 15 18.9033 15 19.5C15 20.0967 14.7629 20.669 14.341 21.091C13.919 21.5129 13.3467 21.75 12.75 21.75C12.1533 21.75 11.581 21.5129 11.159 21.091C10.7371 20.669 10.5 20.0967 10.5 19.5Z" fill="#6D6D6D"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-products">
                  <p>No products found. <Link to="/product-input">Create your first product</Link></p>
                </div>
              )}
            </div>
          </div>
          )}

          {/* Hide/Remove Dropdown Modal */}
          <HideRemoveDropdownModal
            isOpen={dropdownState.isOpen}
            onClose={handleDropdownClose}
            onStatusSelect={handleStatusSelect}
            onEdit={() => handleEditProduct(dropdownState.productId)}
            onDelete={() => handleDeleteProduct(dropdownState.productId)}
            currentStatus={dropdownState.productId ? products.find(p => p.id === dropdownState.productId)?.status : null}
            position={dropdownState.position}
          />

          <div className="pagination">
            <div className="pagination-info">
              Showing {filteredProducts.length > 0 ? startIndex + 1 : 0}-{Math.min(startIndex + itemsPerPage, filteredProducts.length)} of {filteredProducts.length} products
            </div>
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
          </div>
        </div>
      </div>
    </SellerDashboardLayout>
  );
};

export default ProductManagePage;
